import { CLink } from "@/components/common/customLink/CustomLink";
import { cn } from "@/lib/tailwind";
import { useNavContext } from "@/providers/NavProvider";
import { getLangFromLocale } from "@/utils/common";
import { useParams, usePathname } from "next/navigation";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { NavDesktopDropDown } from "./NavDesktopDropDown";
import { useMediaQuery } from "@/hooks/useMediaQuery";

interface IProps {
  hideFlyMenu?: boolean;
  handleSetFlyMenuState?: (val: boolean) => void;
}

export const NavDesktopMenu = ({ hideFlyMenu, handleSetFlyMenuState }: IProps) => {
  // states
  const [showMenu, setShowMenu] = useState(false);
  const [curLink, setCurLink] = useState<null | string>(null);

  // hooks
  const {
    isAtTop,
    isHomePage,
    isScrolling,
    animation,
    duration,
    handleIsLinkHovered,
    isLinkHovered,
    navData,
  } = useNavContext();
  const { locale } = useParams();
  const timeout = useRef<NodeJS.Timeout>();
  const linkRefs = useRef<Map<string, HTMLLIElement>>(new Map());
  const curPath = usePathname();
  const isMobile = useMediaQuery("(max-width: 640px)");

  // consts
  const lang = getLangFromLocale(locale as string);
  const { menu } = navData ?? { menu: { menu: [] } };

  // methods
  function handleShowMenu(link: string, idx: number) {
    if (timeout.current) clearTimeout(timeout.current);
    setShowMenu(true);
    setCurLink(link);
  }

  function handleHideMenu() {
    if (timeout.current) clearTimeout(timeout.current);
    setShowMenu(false);
    setCurLink(null);
  }

  function handleCheckShowMenu(link: string) {
    return curLink ? curLink === link && showMenu : false;
  }

  function handleGetNavLowerClasses() {
    if (isHomePage) {
      if (isAtTop && !isScrolling) return "border-white h-[60px]";
      if (!isAtTop && isScrolling) return "border-transparent h-0";
      return "border-black h-[60px]";
    } else {
      if (isAtTop && !isScrolling) return "border-black h-[60px]";
      if (!isAtTop && !isScrolling) return "border-black h-[60px]";
      return "border-transparent h-0";
    }
  }

  function handleLinkClasses(link: string) {
    if (isHomePage) {
      if (handleCheckShowMenu(link)) {
        if (isAtTop)
          return "after:contents-[''] after:absolute after:-bottom-[0px] after:left-0 after:h-[2px] after:w-full after:bg-current";
        else
          return "md:after:contents-[''] md:after:absolute md:after:-bottom-[0px] md:after:left-0 md:after:h-[2px] md:after:w-full md:after:bg-current md:bg-transparent bg-white";
      }
      if (!isAtTop && !handleCheckShowMenu(link)) return "bg-tertiary-7 md:bg-transparent";
    } else {
      if (handleCheckShowMenu(link)) {
        if (isMobile) return "";
        return "bg-white after:contents-[''] after:absolute after:-bottom-[0px] after:left-0 after:h-[2px] after:w-full after:bg-current";
      } else return "bg-tertiary-7 md:bg-transparent";
    }
  }

  function handleSetActiveLink(isActive: boolean) {
    if (isActive) {
      if (isMobile) return "bg-white";
      return "after:contents-[''] after:absolute after:-bottom-[0px] after:left-1/2 after:h-[2px] after:w-[calc(100%-32px)] after:bg-black after:-translate-x-1/2";
    }
    return "";
  }

  // effects
  useEffect(() => {
    if (hideFlyMenu) {
      setShowMenu(false);
      setCurLink(null);
      handleIsLinkHovered?.(false);
    }
  }, [hideFlyMenu]);

  useLayoutEffect(() => {
    if (!linkRefs || !linkRefs.current) return;
    // @ts-expect-error
    const link = [...linkRefs.current.entries()].find(([key, _]) =>
      curPath.includes(key?.split("/").reverse()[0]),
    );
    if (!link) return;

    link[1].scrollIntoView({
      behavior: "instant",
      inline: "center",
    });
  }, [curPath]);

  return (
    <>
      <div
        id="nav-lower-wrapper"
        className={cn(
          "relative overflow-hidden bg-transparent transition-[height] md:border-t",
          handleGetNavLowerClasses(),
          isLinkHovered ? "border-black" : "",
          animation,
          duration,
        )}
      >
        <ul
          id="nav-lower-links"
          className={cn(
            "no-scrollbar absolute left-1/2 flex h-full w-full -translate-x-1/2 -translate-y-1/2 items-stretch justify-start overflow-x-auto whitespace-nowrap transition-[top] lg:justify-center",
            animation,
            duration,
            isScrolling ? "-top-[28px] md:-top-[30px]" : "top-1/2", // a magic number to satisfy ui requirements :|
          )}
        >
          {menu?.menu?.map((mnu, i) => {
            const linkName = lang === "en" ? mnu.title?.en : mnu.title?.ar;
            const isActive = curPath
              .split("/")
              .reverse()[0]
              .includes(mnu.link.url?.split("/").reverse()[0].split("?")[0]);

            return (
              <li
                ref={(e) => {
                  if (e) linkRefs.current.set(mnu.link.url, e);
                }}
                key={i}
                className={cn(
                  "relative flex items-center px-2",
                  handleLinkClasses(linkName),
                  handleSetActiveLink(isActive),
                )}
                onMouseEnter={() => {
                  handleShowMenu(linkName, i);
                  handleIsLinkHovered?.(true);
                }}
                onMouseLeave={() => {
                  handleHideMenu();
                  handleIsLinkHovered?.(false);
                }}
              >
                <CLink href={mnu.link?.url ?? "/"} className="flex h-full items-center">
                  <p
                    className="px-[25px] text-sm font-semibold md:text-base"
                    style={{
                      color: mnu.color ?? "inherit",
                    }}
                  >
                    {linkName}
                  </p>
                  <NavDesktopDropDown
                    showMenu={
                      handleCheckShowMenu(linkName) &&
                      mnu.subMenu !== null &&
                      mnu.subMenu !== undefined
                    }
                    idx={i}
                  />
                </CLink>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
};
